import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDays, format } from 'date-fns';
import { Button } from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import { usePost } from '../../../apis/ApiRequests';
import Header from '../../../components/uiParts/hygiene/Header';
import TitleBar from '../../../components/uiParts/TitleBar';
import { dateToString } from '../../../utils/Library';
import {
	LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
	Box,
	Card,
	Grid,
	Typography,
	Stack,
} from "@mui/material";
import { facilityData } from '../../../Store';

// ダーク配色
const DARK = {
	pageBg: "#0b1220",
	cardBg: "#111a2e",
	border: "rgba(255,255,255,0.12)",
	text: "rgba(255,255,255,0.92)",
	subText: "rgba(255,255,255,0.70)",
	grid: "rgba(255,255,255,0.12)",
	tooltipBg: "#0f172a",
};

function formatToHHMM(timeStr) {
	if (!timeStr || timeStr.length < 12) return null;
	const hour = timeStr.slice(8, 10);
	const minute = timeStr.slice(10, 12);
	return `${hour}:${minute}`
}

const AqualiumMonitor = () => {

	const { authInfo, setAuthInfo } = useAuth();

	//===============================================
	// initialize
	//===============================================
	//-----------------------------------------------
    // initialize - セッション情報を取得
	//-----------------------------------------------
    const facilityCode   = authInfo.facilityCode ? authInfo.facilityCode : facilityData.facilityCode;
    const facilityName   = authInfo.facilityName;
    const userId         = authInfo.userId ? authInfo.userId : 'Signage';
	const userName = authInfo.userName;
	const authority = authInfo.authority;
	const traderCode = authInfo.traderCode;
	const trader = authInfo.trader;

	const today = format(addDays(new Date(), 0), 'yyyy-MM-dd');
	const navigate = useNavigate();

	//===============================================
	// API
	//===============================================
	//-----------------------------------------------
	// API - searchDataByUnload / Signage
	//-----------------------------------------------
	const { response : searchData_res, post:searchData_post } = usePost();
	const searchData = async () => {
		const numericDate = dateToString(today);
		const postData = {
			'className'    : 'mch.models.table.Signage',
			'methodName'   : 'searchDataByAqualium',
			'facilityCode' : facilityCode,
			'userId'       : userId,
			'entryDate'    : numericDate,
		}
		await searchData_post(postData);
	};
	//-----------------------------------------------
    // API - getDataByHygieneAqualium / Config
	//-----------------------------------------------
    const { response : getThresholdValue_Res, post:getThresholdValue_post } = usePost();
	const getThresholdValue = async () => {
		const postData = {
			'className'    : 'mch.models.system.Config',
			'methodName'   : 'getDataByHygieneAqualium',
			'facilityCode' : facilityCode,
			'userId'       : userId,
		}
		await getThresholdValue_post(postData);
	};
	//===============================================
	// useEffect
	//===============================================
	//-----------------------------------------------
    // useEffect - ロード時の設定
	//-----------------------------------------------
    useEffect(() => {

        searchData();
		getThresholdValue();
        
        // 5秒ごとにデータを更新するタイマーを設定
        const interval = setInterval(searchData, 10000);
    
        // コンポーネントのアンマウント時にタイマーをクリア
        return () => clearInterval(interval);
    },[]);

	//===============================================
	// eventHandler
	//===============================================
	//-----------------------------------------------
    // eventHandler - 日付の変更
	//-----------------------------------------------
    const handleChange_entryDate = (value) => {
    };
	//-----------------------------------------------
    // eventHandler - ホーム画面に戻る
	//-----------------------------------------------
    const handleClick_header = () => {
        userId == 'Signage' ? navigate('/') : navigate('/hygieneHome');
    };
	//===============================================
	// Function
	//===============================================
	//-----------------------------------------------
	// Function - Recharts Tooltip をダーク化
	//-----------------------------------------------
	const DarkTooltip = (props) => {
		const { active, payload, label } = props;
		if (!active || !payload || payload.length === 0) return null;

		return (
			<div style={{
				background: DARK.tooltipBg,
				border: `1px solid ${DARK.border}`,
				borderRadius: 8,
				padding: "10px 12px",
				color: DARK.text,
				boxShadow: "0 6px 18px rgba(0,0,0,0.45)"
			}}>
			<div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
			{payload.map((p) => (
				<div key={p.dataKey} style={{ display: "flex", gap: 8, fontSize: 12, opacity: 0.95 }}>
					<span style={{ width: 10, height: 10, borderRadius: 2, background: p.color, marginTop: 4 }} />
					<span>{p.name}:</span>
					<span style={{ fontWeight: 700 }}>{p.value}</span>
				</div>
			))}
			</div>
		);
	};
	//===============================================
	// JSXのレンダー
	//===============================================
	const data = (searchData_res?.Data ?? []).reduce((acc, { updateDatetime, lineNo, sensorType, value }) => {
		if (!sensorType) return acc;
		const timeKey = formatToHHMM(String(updateDatetime));
		if (!timeKey) return acc;

		if (!acc[lineNo]) acc[lineNo] = {};

		if (!acc[lineNo][timeKey]) {
			acc[lineNo][timeKey] = { time: timeKey };
		}
		acc[lineNo][timeKey][sensorType] = value;
		return acc;
	}, {});

	const dataList = Object.fromEntries(
		Object.entries(data).map(([lineNo, timeMap]) => [
			lineNo,
			Object.values(timeMap).filter(e => e.time).sort((a, b) =>
				a.time.localeCompare(b.time)
			),
		])
	);

	const thresholdsByGraph = (getThresholdValue_Res?.Data ?? []).reduce((acc, {tankNo, sensorType, rangeType, value}) => {
		acc[tankNo] ??= {};
		acc[tankNo][sensorType] ??= {};
		acc[tankNo][sensorType][rangeType] = value;
		return acc
	}, {});

	const isExceed = Object.entries(dataList).map(([lineNo, dataArray]) => {
		const lastData = dataArray[dataArray.length - 1];
		const threshold = thresholdsByGraph[lineNo];
		if(!lastData || !threshold) return false;
		return Object.entries(threshold).some(
			([type, { upper, rower }]) => {
				const value = lastData[type];
				if (value == null) return false;
				if (upper != null && value > upper) return true;
				if (rower != null && value < rower) return true;
				return false;
			}
		)
	})
	
	return (
		<>
			<div className='AqualiumMonitor' style={{ height: '100vh',overflow:'hidden' }}>
				<div  onClick={() => handleClick_header()} style={{backgroundColor:'#0b1220', paddingTop:'0.5em', paddingBottom:'0em'}}>
					<div>
						<span style={{fontSize:'1.5em', fontWeight:'bold', color:'#fff'}}>【水槽情報　{ today }】</span>
					</div>
				</div>
				<Box
					sx={{
						minHeight: "100vh",
						bgcolor: DARK.pageBg,
						px: 2,
						py: 2,
					}}
				>
					<Grid container spacing={2}>
					{[1, 2, 3, 4].map((tankId, index) => (
						<Grid item xs={12} md={6} key={tankId}>
						<Card
							sx={{
							p: 2,
							bgcolor: DARK.cardBg,
							border: `1px solid ${DARK.border}`,
							borderRadius: 2,
							}}
							className= {isExceed[index] ? 'blinking_aqualium' : ""}
						>
							<Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
							<Typography variant="subtitle1" sx={{ color: DARK.text, fontWeight: 800 }}>
								{tankId === 1 ? '掛-18' : tankId === 2 ? '掛-3' : tankId === 3 ? '掛-8' : tankId === 4 ? '掛-13' : `水槽 ${tankId}`}
							</Typography>
							<Typography variant="caption" sx={{ color: DARK.subText }}>
								（水温 / DO / pH / 塩分）
							</Typography>
							</Stack>

							<ResponsiveContainer width="100%" height={240}>
							<LineChart data={dataList[tankId] || []}>
								<XAxis
								dataKey="time"
								tick={{ fill: DARK.subText, fontSize: 12 }}
								axisLine={{ stroke: DARK.grid }}
								tickLine={{ stroke: DARK.grid }}
								/>
								<YAxis
								tick={{ fill: DARK.subText, fontSize: 12 }}
								axisLine={{ stroke: DARK.grid }}
								tickLine={{ stroke: DARK.grid }}
								/>

								<Tooltip content={<DarkTooltip />} />
								<Legend wrapperStyle={{ color: DARK.subText }} />

								<Line dataKey={1} name="水温(℃)" stroke="#4da3ff" dot={false} strokeWidth={2} />
								<Line dataKey={2} name="DO(%)" stroke="#43d17a" dot={false} strokeWidth={2} />
								<Line dataKey={3} name="pH" stroke="#ffb14a" dot={false} strokeWidth={2} />
								<Line dataKey={4} name="塩分(%)"  stroke="#ff5d34" dot={false} strokeWidth={2} />
							</LineChart>
							</ResponsiveContainer>
						</Card>
						</Grid>
					))}
					</Grid>
				</Box>
			</div>
		</>
	);
};

export default AqualiumMonitor;
